import { styled, Box, Alert, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import UPButton from 'components/button';
import { UPDropdown } from 'components/dropdown';
import { FilterButton } from 'components/filters-menu/styles';
import { StatusTagTone } from './types';
import { ReactComponent as EmployeeIcon } from '../../assets/icons/EmployeeIcon.svg';
import { ReactComponent as InviteIcon } from '../../assets/icons/InviteIcon.svg';
import { ReactComponent as DownloadIcon } from '../../assets/icons/DownloadGreen.svg';

const CARD_MIN_HEIGHT = 256;

export const EmployeeCardStyled = styled(Box)(() => ({
  width: '100%',
  minWidth: 320,
  minHeight: CARD_MIN_HEIGHT,
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.025)',
  borderRadius: '8px',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
}));

export const CardContent = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  background: 'white',
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  flexDirection: 'column',
  color: theme.palette.secondary.contrastText,
  padding: '24px 20px 20px',
}));

export const MoreContent = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  background: theme.palette.primary.light,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  color: theme.palette.secondary.dark,
  padding: '24px 20px',
}));

export const CardAvatar = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: '18px',
}));

export const EmployeeName = styled(Box)(() => ({
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '20px',
  width: '100%',
  maxWidth: '100%',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const CardMetaGroup = styled(Box)(() => ({
  marginTop: '6px',
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

export const CardMetaLine = styled(Box)(() => ({
  fontSize: '12px',
  lineHeight: '16px',
  width: '100%',
  maxWidth: '100%',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

// 16px sets the card's one deliberate break: name and the gender/worker-class lines read as one
// identity block (6px apart), then a wider gap before the contact lines.
export const CardContactGroup = styled(Box)(() => ({
  marginTop: '16px',
  width: '100%',
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '4px',
}));

export const PersonalDetails = styled(Box)(() => ({
  fontSize: '12px',
  lineHeight: '16px',
  marginBottom: '16px',
  textAlign: 'center',
}));

export const ContactDetails = styled(Box)(({ theme }) => ({
  fontSize: '12px',
  lineHeight: '16px',
  color: theme.palette.secondary.dark,
  width: '100%',
  maxWidth: '100%',
  textAlign: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));

export const InviteActionsContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  width: '100%',
  gap: '16px',
  marginTop: '10px',
}));

/**
 * Resend / Copy on an invited employee's card. The shared UPButton is built for page-level actions
 * (24px side padding, vertical padding that grows with the label), which on a ~190px card made the
 * label break onto a second line and the button grow taller than the row.
 *
 * So: `nowrap` keeps every label on one line and the height is pinned to 40px, which is exactly the
 * 10px padding + 20px line-height + 10px padding the design asks for - a longer label therefore
 * grows the button sideways only. `minWidth: auto` drops MUI's 64px floor, and `flexShrink: 0` means
 * a pair too wide for the card wraps onto a second row (see the container) rather than compressing.
 */
export const InviteActionButton = styled(UPButton)(({ theme }) => ({
  '&&': {
    height: '40px',
    minWidth: 'auto',
    padding: '10px 24px',
    flexShrink: 0,
    fontSize: '14px',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    fontFamily: theme.typography.fontFamily,
  },
}));

export const InviteEmployeesButton = styled('span')(() => ({
  height: '25px',
  display: 'flex',
}));

export const InviteEmployeesButtonText = styled('span')(() => ({
  marginLeft: '8px',
}));

export const UPDropdownStyled = styled(UPDropdown)(() => ({
  maxWidth: '140px',
  display: 'inline-block',
  marginLeft: '8px',
}));

export const UPButtonStyled = styled(UPButton)(() => ({
  marginLeft: '8px',
}));

export const RequiresActionContainer = styled(Box)(() => ({
  position: 'absolute',
  top: '16px',
  right: '16px',
  animation: 'breatheAnimation 3s ease-in-out infinite',
  '@keyframes breatheAnimation': {
    '0%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.2,
    },
    '100%': {
      opacity: 1,
    },
  },
}));

export const InactiveContainer = styled(Box)(() => ({
  position: 'absolute',
  top: '16px',
  right: '40px',
}));

export const InvitedContainer = styled(Box)(() => ({
  position: 'absolute',
  top: '16px',
  right: '40px',
}));

export const FiltersEmployeeBox = styled(Box)(({ theme }) => ({
  width: '140px',
  marginRight: '16px',
  [theme.breakpoints.down(690)]: {
    display: 'none',
  },
}));

export const ResponsiveFiltersEmployeeBox = styled(Box)(({ theme }) => ({
  width: '280px',
  marginRight: '8px',
  [theme.breakpoints.up(691)]: {
    width: '140px',
  },
}));

export const NameEmployeeBox = styled(Box)(({ theme }) => ({
  width: '280px',
  marginLeft: theme.spacing(1),
  [theme.breakpoints.down(712)]: {
    marginLeft: '0px',
  },
}));

export const CollapseSearchFiltersContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.up(691)]: {
    display: 'none',
  },
}));

export const AddInviteButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down(690)]: {
    width: 'unset',
    alignItems: 'start',
    marginBottom: '16px',
  },
}));

export const InviteEmployeeButton = styled(UPButton)(({ theme }) => ({
  margin: '16px 0px',
  [theme.breakpoints.down(690)]: {
    margin: '6px 0px',
  },
}));

export const AddEmployeeButton = styled(UPButton)(({ theme }) => ({
  margin: '16px 16px 16px 0px',
  height: '48px',
  [theme.breakpoints.down(690)]: {
    margin: '6px 8px 6px 0px',
    fontSize: '14px',
  },
}));

export const AddEmployeeButtonDashboard = styled(UPButton)(({ theme }) => ({
  width: '100%',
  height: '45%',
  fontSize: '15px',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down(690)]: {
    margin: '6px 8px 6px 0px',
    fontSize: '14px',
  },
}));

export const AlertStyled = styled(Alert)(({ theme }) => ({
  width: '380px',
  [theme.breakpoints.down(690)]: {
    width: '100%',
  },
}));

export const RequiresActionButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginLeft: '8px',
  [theme.breakpoints.down(690)]: {
    marginLeft: 'unset',
    marginTop: '16px',
    width: '75%',
  },
}));

export const EmployeesSwitchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  marginBottom: theme.spacing(2),
}));

/**
 * Wraps the two segments only for layout (`role="tablist"`) — unlike the previous pill design,
 * this control has no background or padding of its own; each segment draws its own border.
 */
export const EmployeesSwitchTrack = styled(Box)(() => ({
  display: 'inline-flex',
  alignItems: 'center',
}));

/**
 * Figma node 7264-62811: two bordered segments meeting in the middle, rounded only on the outer
 * corner of the pair. Colors are `theme.palette.primary.main` (#008C15) selected and
 * `theme.palette.secondary.main` (#9EA2A2) unselected — confirmed against src/theme/index.ts
 * rather than hardcoded, since they happen to match the design's hex values exactly.
 */
export const EmployeesSwitchSegment = styled('button')<{ selected: boolean }>(({ theme, selected }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '120px',
  height: '48px',
  padding: '0 16px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontSize: '13px',
  lineHeight: '20px',
  fontWeight: selected ? 600 : 500,
  border: `1px solid ${selected ? theme.palette.primary.main : theme.palette.secondary.main}`,
  backgroundColor: selected ? '#FFFFFF' : 'transparent',
  color: selected ? theme.palette.primary.main : theme.palette.secondary.main,
  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: selected ? '#FFFFFF' : alpha(theme.palette.secondary.light, 0.4),
  },
  // Rounds only the pair's outer corners; the shared inner edge stays square on both segments.
  '&:first-of-type': {
    borderRadius: '4px 0 0 4px',
  },
  '&:last-of-type': {
    borderRadius: '0 4px 4px 0',
    // Sits over the first segment's right border so the shared edge reads as one 1px line
    // instead of two borders doubling up to 2px.
    marginLeft: '-1px',
  },
}));

export const FiltersContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  [theme.breakpoints.down(690)]: {
    width: 'unset',
    alignItems: 'start',
  },
}));

// Square variant of the shared FilterButton, scoped to the employees page, to match the mobile look & feel.
export const SquareFilterButton = styled(FilterButton)(() => ({
  '&.MuiButton-root': {
    width: '48px',
    minWidth: '48px',
    height: '48px',
    padding: 0,
  },
}));

const defaultButtonColor = ({ theme }) => ({
  fill: 'white',
  'button:hover &': {
    fill: theme.palette.primary.main,
  },
});

export const StyledEmployeeIcon = styled(EmployeeIcon)(defaultButtonColor);

export const StyledInviteIcon = styled(InviteIcon)(defaultButtonColor);

export const StyledDownloadIcon = styled(DownloadIcon)(defaultButtonColor);

export const FiltersLeftContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  [theme.breakpoints.down(999)]: {
    flexWrap: 'wrap',
  },
  [theme.breakpoints.down(690)]: {
    flexDirection: 'column',
  },
}));

export const FiltersRightContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row-reverse',
  alignItems: 'flex-end',
  [theme.breakpoints.down(690)]: {
    flexDirection: 'column',
  },
}));

export const SortIconBox = styled(Box)<{ sortOrder: number }>(({ theme, sortOrder }) => ({
  transform: `rotate(${180 * sortOrder}deg)`,
  transition: 'transform 0.2s ease-in-out',
  cursor: 'pointer',
  alignSelf: 'center',
  marginTop: theme.spacing(1),
  [theme.breakpoints.down(691)]: {
    display: 'none',
  },
}));

export const SortIconMobileBox = styled(Box)<{ sortOrder: number }>(({ theme, sortOrder }) => ({
  transform: `rotate(${180 * sortOrder}deg)`,
  transition: 'transform 0.2s ease-in-out',
  cursor: 'pointer',
  alignSelf: 'center',
  marginTop: theme.spacing(4),
  [theme.breakpoints.up(691)]: {
    display: 'none',
  },
}));

export const ApplicantStatusIndicator = styled(Box, {
  shouldForwardProp: prop => prop !== 'isDiscarded',
})<{ isDiscarded?: boolean }>(({ theme, isDiscarded }) => ({
  position: 'absolute',
  top: '16px',
  right: '16px',
  fontSize: '11px',
  fontWeight: 700,
  textTransform: 'uppercase',
  color: isDiscarded ? theme.palette.error.main : theme.palette.secondary.main,
}));

export const GreyedOutAvatar = styled(Box, {
  shouldForwardProp: prop => prop !== 'isGreyedOut',
})<{ isGreyedOut?: boolean }>(({ isGreyedOut }) => ({
  display: 'inline-flex',
  filter: isGreyedOut ? 'grayscale(1)' : 'none',
}));

export const CardDivider = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '1px',
  backgroundColor: theme.palette.info.dark,
  margin: '8px 0',
}));

/**
 * Bottom-centred status tag introduced by US 581 ("update the indicators to tags"). It replaces the
 * corner icons (invited hourglass, not-eligible triangle) and the applicant's corner text label, so
 * every card carries exactly one tag in the same place on web and mobile.
 *
 * Geometry and typography come from the tag nodes in Figma (Invited 7302:62987, Requirements
 * 7302:63686, Ready 7344:64143, Applied 7344:64431, Discarded 7303:63719): a fully rounded pill,
 * 24px tall, 12px side padding, a 7px gap after the icon, and the label in 10px SemiBold uppercase.
 *
 * Every one of those pills is filled #F5F6F6 (info.main) - the fill does NOT vary by status. Only
 * the label colour and the glyph change, which is why the tone below carries a colour and nothing
 * else, and why the icons inherit it through `currentColor`.
 */
/** Discarded and Terminated use the design's own deeper red, not the theme's error.main (#EC4A67)
 * which is tuned for alerts and reads too hot behind a grey pill. */
const STATUS_TAG_RED = '#B0394D';

const STATUS_TAG_TONES: Record<StatusTagTone, (theme: Theme) => string> = {
  positive: theme => theme.palette.primary.main,
  neutral: theme => theme.palette.secondary.dark,
  warning: theme => theme.palette.warning.main,
  negative: () => STATUS_TAG_RED,
};

export const StatusTagContainer = styled(Box)(() => ({
  marginTop: 'auto',
  paddingTop: '12px',
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
}));

export const StatusTag = styled(Box, {
  shouldForwardProp: prop => prop !== 'tone',
})<{ tone: StatusTagTone }>(({ theme, tone }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '7px',
  maxWidth: '100%',
  height: '24px',
  padding: '0 12px',
  borderRadius: '12px',
  fontSize: '10px',
  fontWeight: 600,
  lineHeight: '14px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  backgroundColor: theme.palette.info.main,
  color: STATUS_TAG_TONES[tone](theme),
}));

/** Each icon carries its own designed dimensions (the envelope is 15x10, the applicant glyphs are
 * ~21px round, the checkmark 11x10), so no size is imposed here - one global size would distort
 * unlike assets. The slot only stops the flex row from stretching them and lets the tone through:
 * the committed SVGs paint their glyph in `currentColor`, keeping their #F5F6F6 knockouts literal. */
export const StatusTagIcon = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  '& svg': {
    display: 'block',
  },
}));

/**
 * The card grid. Cards have a 320px minimum, so the column count cannot be fixed per breakpoint the
 * way it used to be (`lg={2}` gave six ~225px columns, which a 320px card would have overflowed).
 * auto-fill fits as many 320px tracks as the viewport allows and reflows on its own; the min() keeps
 * a single track from overflowing a viewport narrower than 320px.
 *
 * `alignItems: start` matters: cards size to their content (272px for an employee, 292px when it is
 * invited, 234px for an applicant), and stretching them to the tallest in the row would reopen the
 * gap under the status tag that is supposed to be exactly 20px.
 */
export const CardsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
  alignItems: 'start',
  gap: theme.spacing(1),
  width: '100%',
  marginTop: theme.spacing(2),
}));
